import React, { useState } from "react";
import {
  Space,
  Button,
  Row,
  Col,
  notification,
  Typography,
  TabsProps,
  Tabs,
  Modal,
  Input,
  Tooltip,
  Dropdown
} from "antd";

import type { ColumnsType } from "antd/es/table";
import {
  LoadingOutlined,
  EditOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  CloudDownloadOutlined,
  ShareAltOutlined,
  SendOutlined,
  DollarOutlined,
  RollbackOutlined,
  CheckCircleOutlined,
  MailOutlined,
  FilePdfOutlined,
  FilePdfFilled,
  CloudUploadOutlined,
  CloudFilled,
  UploadOutlined,
  PayCircleOutlined,
  CopyOutlined,
  CarOutlined
} from "@ant-design/icons";
import PreviewAndSendEmailModal from "../FinanceSurvey/PreviewAndSendEmailModal";

import {
  useGetSurveysQuery,
  useUpdateSurveyMutation,
  useGeneratePaymentLinkMutation
} from "../../../redux/services/surveyAPI";
import {
  useCreatePaymentMutation,
  useCreateRefundMutation,
  useSendCustomerReportMutation,
  useSendQuotationPdfMutation,
  useViewQuotationReportMutation,
  useViewCustomerReportMutation
} from "../../../redux/services/assignmentApi";
import { useBulkCustomerReportsMutation } from "../../../redux/services/reportApi";
import AddPaymentModal from "../FinanceSurvey/AddPaymentModal";
import ErrorDetails from "../../../components/ErrorDetails";
import Filters from "../Filters";
import { Link, useNavigate } from "react-router-dom";
import {
  localizeDateTime,
  isUserHasRoleTypes,
  getStatusText,
  getQuerySearchParams,
  applyPaginationToQueryParams
} from "../../../utils";
import { BASE_URL } from "../../../redux/services/baseApi";
import { useAppSelector } from "../../../redux/hooks";
import UploadInvoicePdfModal from "./UploadInvoicePdfModal";
import TopScrollTable from "../../../components/TopScrollTable";
import { EMAIL_TYPES } from "../../../constants/helperConstants";

interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  tags: string[];
  pending_amount: number;
  cable_length_new: number;
  complementary_cable_length: number;
}

interface Survey {
  id: string;
  ecb_survey_code: string;
}

const items: TabsProps["items"] = [
  {
    key: "1",
    label: "All Surveys"
  },
  {
    key: "2",
    label: "Unpaid Surveys"
  },
  {
    key: "3",
    label: "Paid Surveys"
  }
];

const { Text } = Typography;

const Survey = () => {
  const querySearchParams = getQuerySearchParams();
  const [queryParams, setQueryParams] = useState<any>({
    include_all: true,
    ecb_code: parseInt(querySearchParams.ecb_code),
    page: parseInt(querySearchParams.page) || 1,
    page_size: parseInt(querySearchParams.page_size) || 20
  });
  const navigate = useNavigate();

  const auth = useAppSelector<any>((store) => store.auth);
  const [showModal, setShowModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<Survey | null>(null);
  const [api, contextHolder] = notification.useNotification();
  const [tabKey, setTabKey] = useState("1");
  const [paymentType, setPaymentType] = useState("");
  const [emailType, setEmailType] = useState("seek_payment_email");
  const [showEmailPreviewModal, setShowEmailPreviewModal] = useState(false);
  const [showInvoiceUploadModal, setShowInvoiceUploadModal] = useState(false);

  const [createPayment] = useCreatePaymentMutation();
  const [createRefund] = useCreateRefundMutation();

  const [onSendQuotationPdf] = useSendQuotationPdfMutation();
  const [onViewQuotationReport] = useViewQuotationReportMutation();
  const [viewCustomerReport] = useViewCustomerReportMutation();
  const [downloadInProgress, setDownloadInProgress] = useState<any>({});
  const [bulkReportLoading, setBulkReportLoading] = useState(false);
  const [bulkCustomerReports] = useBulkCustomerReportsMutation();

  const { data, isLoading, refetch, isFetching } = useGetSurveysQuery({
    ...queryParams,
    include_all: true
  });
  const surveys = data?.surveys || [];

  const user = auth.user;
  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "management"].includes(role.role_type)
  );

  const isInvoiceUploadingUser = user?.roles?.some(
    (role: { role_type: string }) =>
      ["mis", "super_user", "finance_controller"].includes(role.role_type) // management generally not considered editor user
  );

  const isCityManager = user?.roles?.some((role: { role_type: string }) =>
    ["city_manager"].includes(role.role_type)
  );

  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );

  const isZonalManager = user?.roles?.some((role: { role_type: string }) =>
    ["zonal_manager"].includes(role.role_type)
  );

  const isEditorUser = user?.roles?.some((role: { role_type: string }) =>
    ["finance_controller", "mis", "super_user"].includes(role.role_type)
  );

  const isClient = user?.user_type === "client";

  const isSuperUser = (user: any) => {
    return isUserHasRoleTypes(user, ["super_user"]);
  };

  const onShowAddPaymentModal = (survey: any, payType: string) => {
    setSelectedSurvey(survey);
    setPaymentType(payType);
    setShowModal(true);
  };

  const onShowEmailPreviewModal = (survey: any, eType: string) => {
    setEmailType(eType);
    setSelectedSurvey(survey);
    setShowEmailPreviewModal(true);
  };

  const onShowInvoiceUploadModal = (survey: any) => {
    setSelectedSurvey(survey);
    setShowInvoiceUploadModal(true);
  };

  const onShowPaymentLinkModal = (survey: any) => {
    onGeneratePaymentLink(survey);
  };

  const onAddPayment = async (values: object, setLoading: any, form: any) => {
    try {
      setLoading(true);
      if (paymentType === "refund") {
        await createRefund(values).unwrap();
      } else if (paymentType === "payment") {
        await createPayment(values).unwrap();
      }
      api.success({
        message: `Add ${paymentType} to survey successful`,
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      setShowModal(false);
      refetch();
    } catch (error: any) {
      api.info({
        message: `Add ${paymentType} to survey failed`,
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const [updateSurvey] = useUpdateSurveyMutation();
  const [generatePaymentLink] = useGeneratePaymentLinkMutation();

  const [sendCustomerReport] = useSendCustomerReportMutation();

  const onSendCustomerReport = async (surveyId: any) => {
    try {
      await sendCustomerReport(surveyId).unwrap();
      api.success({
        message: "Closure report send successfully",
        placement: "top"
      });
      refetch();
    } catch (error: any) {
      api.error({
        message: "Closure report send operation failed",
        placement: "top"
      });
    }
  };

  const onViewCustomerReport = async (surveyId: any) => {
    try {
      const res = await viewCustomerReport(surveyId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
      refetch();
    } catch (error: any) {
      api.error({
        message: "MSIL SR report operation failed",
        placement: "top"
      });
    }
  };

  const sendQuotationPdf = async (surveyId: any) => {
    try {
      await onSendQuotationPdf(surveyId).unwrap();
      api.success({
        message: "Send Quotation Pdf Operation Completd Successfully",
        placement: "top"
      });
    } catch (error: any) {
      api.info({
        message: "Send Quotation Pdf Operation failed",
        placement: "top"
      });
    }
  };

  const viewQuotationReport = async (surveyId: any) => {
    try {
      const res = await onViewQuotationReport(surveyId).unwrap();
      if (res) {
        window.open(res.pdf_url, "_blank");
      }
    } catch (error: any) {
      api.info({
        message: "View Quotation Operation failed",
        placement: "top"
      });
    }
  };

  const onSendEmail = async (values: any, setLoading: any, form: any, fileList: any) => {
    setLoading(true);

    try {
      const id = values["id"];
      const survey = new FormData();

      for (const key in values) {
        if (values?.[key]) {
          survey.append(`survey[${key}]`, values[key]);
        }
      }

      fileList.forEach((file: any) => {
        survey.append("survey[attachments][]", file);
      });

      const response = await fetch(`${BASE_URL}surveys/${id}/trigger_email`, {
        method: "POST",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: survey
      });

      const responseData = await response.json();

      if (responseData?.status === "Successful") {
        setLoading(false);
        api.success({
          message: "Email Sent!",
          placement: "top"
        });
        setShowEmailPreviewModal(false);
        setSelectedSurvey(null);
        refetch();
      } else {
        setLoading(false);
        api.info({
          message: "Email sending failed!",
          description: <ErrorDetails errors={responseData || []} />,
          placement: "top"
        });
      }
    } catch (error: any) {
      setLoading(false);
      api.info({
        message: "Email sending failed!",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const onUploadInvoicePdf = async (values: any, setLoading: any, form: any, fileList: any) => {
    setLoading(true);

    try {
      const id = values["id"];
      const survey = new FormData();

      for (const key in values) {
        if (values?.[key]) {
          survey.append(`survey[${key}]`, values[key]);
        }
      }

      fileList.forEach((file: any) => {
        survey.append("survey[attachment]", file);
      });

      const response = await fetch(`${BASE_URL}print_reports/${id}/upload_invoice_pdf`, {
        method: "POST",
        headers: {
          "access-token": auth.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        },
        body: survey
      });

      const responseData = await response.json();

      if (responseData?.pdf_url) {
        setLoading(false);
        api.success({
          message: "Invoice Pdf Upload Successful!",
          placement: "top"
        });
        setShowInvoiceUploadModal(false);
        setSelectedSurvey(null);
        refetch();
      } else {
        setLoading(false);
        setShowInvoiceUploadModal(false);
        setSelectedSurvey(null);
        api.error({
          message: "Invoice PDF upload failed",
          placement: "top"
        });
      }
    } catch (error: any) {
      setLoading(false);
      setShowInvoiceUploadModal(false);
      setSelectedSurvey(null);
      api.error({
        message: "Invoice PDF upload failed",
        placement: "top"
      });
    }
  };

  const onPaid = async (id: any) => {
    try {
      const values = { payment_received: true };
      const data = { ...values };
      await updateSurvey({ id, survey: data }).unwrap();
      api.success({
        message: "Survey marked paid successfully!",
        placement: "top"
      });
      refetch();
    } catch (error: any) {
      api.info({
        message: "Update Survey failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
    }
  };

  const handleCopy = (value: string) => {
    try {
      navigator.clipboard.writeText(value);
      api.success({ message: "Copied to clipboard!" });
    } catch (err) {
      api.error({ message: "Failed to copy!" });
    }
  };

  const onGeneratePaymentLink = async (survey: any) => {
    try {
      const res = await generatePaymentLink(survey?.id).unwrap();
      if (res.payment_link) {
        Modal.info({
          title: `Payment link for ${survey?.ecb_survey_code}`,
          content: (
            <>
              <Input
                value={res.payment_link}
                disabled
                suffix={
                  <Tooltip title="Copy to clipboard">
                    <CopyOutlined
                      onClick={() => handleCopy(res.payment_link)}
                      style={{ cursor: "pointer", padding: "0 10px" }}
                    />
                  </Tooltip>
                }
              />
            </>
          ),
          okText: "Close",
          width: 600,
          onOk() {
            setSelectedSurvey(null);
          }
        });
      } else {
        api.error({
          message: "Payment link is not available"
        });
      }
    } catch (error: any) {
      api.error({
        message: "Payment link generation failed"
      });
    }
  };

  const onCancel = () => {
    setShowModal(false);
    setShowEmailPreviewModal(false);
    setShowInvoiceUploadModal(false);
    setSelectedSurvey(null);
    setPaymentType("");
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Survey Date",
      dataIndex: "scheduled_survey_date_time",
      key: "scheduled_survey_date_time",
      render: (scheduled_survey_date_time) => (
        <Text>
          {scheduled_survey_date_time ? localizeDateTime(scheduled_survey_date_time) : "-"}
        </Text>
      )
    },
    {
      title: "Brand Code",
      dataIndex: "brand",
      key: "brand_code",
      render: (brand) => <Text>{brand ? brand.code : "-"}</Text>
    },
    {
      title: "Cable length",
      dataIndex: "cable_length_new",
      key: "cable_length_new"
    },
    {
      title: "ECB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code",
      render: (ecb_booking_code) => <>{ecb_booking_code}</>
    },
    {
      title: "Survey Code",
      dataIndex: "ecb_survey_code",
      key: "ecb_survey_code",
      render: (text, survey) => {
        let color = "inherit";
        let reason = "";
        if (survey.pending_amount > 0) {
          color = "red";
          reason = "Pending amount exists";
        }
        else if (survey.complementary_cable_length !== null && survey.cable_length_new !== null) {
          if (survey.cable_length_new > survey.complementary_cable_length) {
            color = "red";
            reason = "Cable length exceeds complimentary limit";
          }
        }
        return (
          <span style={{ color }} title={reason}>
            {text}
          </span>
        );
      }
    },
    {
      title: "Brand Booking Status",
      dataIndex: "brand_booking_status",
      key: "brand_booking_status"
    },
    {
      title: "EV City",
      dataIndex: "city",
      key: "city",
      render: (city) => <>{city?.name}</>
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      render: (zone) => <>{zone?.name}</>
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "City Manager",
      dataIndex: "assigned_city_manager",
      key: "assigned_city_manager",
      render: (assigned_city_manager) => <>{assigned_city_manager?.name || "-"}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "MSIL SR Sending Status",
      dataIndex: "survey_report_sent_to_api",
      key: "survey_report_sent_to_api",
      render: (_, survey: any) => (
        <>
          {survey?.brand?.send_reports_to_api && survey?.status === "completed"
            ? survey?.survey_report_sent_to_api
              ? "Sent"
              : "Pending"
            : ""}
        </>
      )
    },
    {
      title: "MSIL Quote Sending Status",
      dataIndex: "quotation_sent_to_api",
      key: "quotation_sent_to_api",
      render: (_, survey: any) => (
        <>
          {survey?.brand?.send_reports_to_api && survey?.status === "completed"
            ? survey?.quotation_sent_to_api
              ? "Sent"
              : "Pending"
            : ""}
        </>
      )
    },
    {
      title: "Partner",
      dataIndex: "survey_partner",
      key: "survey_partner",
      render: (survey_partner) => <>{survey_partner?.name || "-"}</>
    },
    {
      title: "Survey Status",
      dataIndex: "status",
      key: "status",
      render: (_, survey: any) => <>{getStatusText(survey)}</>
    },
    {
      title: "Customer Rating",
      dataIndex: "customer_rating",
      key: "customer_rating",
      render: (_, survey: any) => (
        <>
          {survey.customer_rating && survey?.status === "completed"
            ? `${survey.customer_rating}/5`
            : "-"}
        </>
      )
    },
    {
      title: "Installations",
      key: "installations",
      dataIndex: "installations",
      render: (installations: any[]) => (
        <>
          {installations?.length > 0 &&
            installations?.map((installation) => (
              <Text style={{ whiteSpace: "nowrap" }} key={installation?.id}>
                <Link to={`/installations?ecb_code=${installation.ecb_install_code}`}>
                  {`${installation?.ecb_install_code} - ${installation?.status.toUpperCase()}`}
                </Link>
                <br />
              </Text>
            ))}
          {installations?.length === 0 && (
            <Text style={{ whiteSpace: "nowrap" }}>
              -
              <br />
            </Text>
          )}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, survey: any) => (
        <Space size="middle">
          <>
            <Link to={`${survey?.id}`}>
              <Button icon={<EyeOutlined />} title="View"></Button>
            </Link>
            {((survey?.status === "completed" &&
              (isAdminUser ||
                isMisUser ||
                ((isCityManager || isZonalManager) && !survey?.brand?.send_reports_to_api))) ||
              survey?.status !== "completed") && (
                <Link to={`${survey?.id}/update`}>
                  <Button icon={<EditOutlined />} title="Update"></Button>
                </Link>
              )}
            {survey?.status !== "cancelled" && (
              <>
                {(isAdminUser || isMisUser) && (
                  <Link to={`/${survey?.id}/print-survey`} target="_blank">
                    <Button icon={<PrinterOutlined />} title="Print"></Button>
                  </Link>
                )}
                <Link to={`/${survey?.id}/share-survey`} target="_blank">
                  <Button icon={<ShareAltOutlined />} title="Share"></Button>
                </Link>
                <Link to={`/${survey?.id}/customer-report-survey`} target="_blank">
                  <Button icon={<SendOutlined />} title="Share"></Button>
                </Link>
              </>
            )}
            {survey?.status === "completed" && (
              <>{(survey?.installations?.length == 0 || isSuperUser(user) || survey?.brand_model?.allow_multiple_installations) && (
                <Link to={`${survey?.id}/create-installation`}>
                  <Button icon={<PlusCircleOutlined />} title="Create Installation"></Button>
                </Link>)}
                {survey?.zip_file ? (
                  <Link to={survey?.zip_file} target="_blank">
                    <Button title="Download Zip">
                      <CloudDownloadOutlined />
                    </Button>
                  </Link>
                ) : (
                  <Button
                    disabled={
                      Object.prototype.hasOwnProperty.call(downloadInProgress, survey?.id) &&
                      downloadInProgress[survey?.id] === true
                    }
                    title="Download Zip"
                    onClick={() => handleDownload(survey?.id, survey?.ecb_survey_code)}
                  >
                    {Object.prototype.hasOwnProperty.call(downloadInProgress, survey?.id) &&
                      downloadInProgress[survey?.id] === true ? (
                      <LoadingOutlined style={{ fontSize: 12 }} spin />
                    ) : (
                      <CloudDownloadOutlined />
                    )}
                  </Button>
                )}
                {survey?.payment_received && (
                  <Button
                    icon={<MailOutlined />}
                    onClick={() => onShowEmailPreviewModal(survey, "refund_processed_email")}
                    title="Send Refund Processed Email"
                  ></Button>
                )}
              </>
            )}
          </>
          {isSuperUser(user) && survey?.status === "completed" && tabKey === "2" && (
            <>
              <Button
                icon={<MailOutlined />}
                onClick={() => onShowEmailPreviewModal(survey, "seek_payment_email")}
                title={survey.pricing_expired ? "Pricing has Expired" : "Send Seek Payment Email"}
                disabled={survey.pricing_expired}
              ></Button>
              <Button
                icon={<DollarOutlined />}
                onClick={() => onShowAddPaymentModal(survey, "payment")}
                title="Add Payment"
                disabled={survey?.payment_count < 3 ? false : true}
              ></Button>
              <Button
                icon={<RollbackOutlined />}
                onClick={() => onShowAddPaymentModal(survey, "refund")}
                title="Add Refund"
              ></Button>
              <Button
                icon={<MailOutlined />}
                onClick={() => onShowEmailPreviewModal(survey, "refund_email")}
                title={survey.pricing_expired ? "Pricing has Expired" : "Send Refund Email"}
                disabled={survey.pricing_expired}
              ></Button>
              <Button
                icon={<CheckCircleOutlined />}
                title="Mark Paid"
                onClick={() => {
                  Modal.confirm({
                    title: "Confirm",
                    content: `Mark paid ${survey.ecb_survey_code} ?`,
                    okText: "Yes",
                    onOk() {
                      onPaid(survey.id);
                    }
                  });
                }}
              ></Button>
              <Button
                icon={<PayCircleOutlined />}
                title="Generate Payment Link"
                onClick={() => onShowPaymentLinkModal(survey)}
              ></Button>
            </>
          )}
          {survey?.brand?.send_reports_to_api && survey?.status === "completed" && (
            <>
              <Button
                icon={<FilePdfOutlined />}
                title="MSIL SR report PDF"
                onClick={() => onViewCustomerReport(survey.id)}
              ></Button>
              <Button
                icon={<CloudUploadOutlined />}
                title="Send MSIL SR report"
                onClick={() => onSendCustomerReport(survey.id)}
              ></Button>
              <Button
                icon={<FilePdfFilled />}
                title="View Quotation PDF"
                onClick={() => viewQuotationReport(survey.id)}
              ></Button>
              {isEditorUser && (
                <>
                  <Button
                    icon={<CloudFilled />}
                    title="Send Quotation PDF"
                    onClick={() => sendQuotationPdf(survey.id)}
                  ></Button>
                  <Button
                    icon={<UploadOutlined />}
                    title="Upload Invoice PDF"
                    onClick={() => onShowInvoiceUploadModal(survey)}
                  ></Button>
                </>
              )}
            </>
          )}
        </Space>
      )
    }
  ];

  const financeColumns = [
    {
      title: "Base Amount",
      dataIndex: "base_amount",
      key: "base_amount"
    },
    {
      title: "GST Amount",
      dataIndex: "gst_amount",
      key: "gst_amount"
    },
    {
      title: "Invoice Total",
      dataIndex: "invoice_total",
      key: "invoice_total"
    },
    {
      title: "Amount to Receive",
      dataIndex: "amount_to_receive",
      key: "amount_to_receive"
    },
    {
      title: "Amount to Refund",
      dataIndex: "amount_to_refund",
      key: "amount_to_refund"
    }
  ];

  if (isSuperUser(user) && (tabKey == "2" || tabKey == "3")) {
    const lastIndex = 12; // 12 is index of column before action column
    columns.splice(lastIndex + 1, 0, ...financeColumns); // for insert columns at after an specified index
  }

  const clientColumns: ColumnsType<DataType> = [
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name"
    },
    {
      title: "Contact Number",
      dataIndex: "contact_number",
      key: "contact_number"
    },
    {
      title: "Survey Date",
      dataIndex: "scheduled_survey_date_time",
      key: "scheduled_survey_date_time",
      render: (scheduled_survey_date_time) => (
        <Text>
          {scheduled_survey_date_time ? localizeDateTime(scheduled_survey_date_time) : "-"}
        </Text>
      )
    },
    {
      title: "Brand Code",
      dataIndex: "brand",
      key: "brand_code",
      render: (brand) => <Text>{brand ? brand.code : "-"}</Text>
    },
    {
      title: "ECB Booking Code",
      dataIndex: "ecb_booking_code",
      key: "ecb_booking_code",
      render: (ecb_booking_code) => <>{ecb_booking_code}</>
    },
    {
      title: "Survey Code",
      dataIndex: "ecb_survey_code",
      key: "ecb_survey_code"
    },
    {
      title: "Customer City",
      dataIndex: "customer_city",
      key: "customer_city"
    },
    {
      title: "Zone",
      dataIndex: "zone",
      key: "zone",
      render: (zone) => <>{zone?.name}</>
    },
    {
      title: "Charger Type",
      dataIndex: "obc",
      key: "obc"
    },
    {
      title: "Survey Status",
      dataIndex: "status",
      key: "status",
      render: (_, survey: any) => <>{getStatusText(survey)}</>
    },
    {
      title: "Installations",
      key: "installations",
      dataIndex: "installations",
      render: (installations: any[]) => (
        <>
          {installations?.length > 0 &&
            installations?.map((installation) => (
              <Text style={{ whiteSpace: "nowrap" }} key={installation?.id}>
                <Link to={`/installations?ecb_code=${installation.ecb_install_code}`}>
                  {`${installation?.ecb_install_code} - ${installation?.status.toUpperCase()}`}
                </Link>
                <br />
              </Text>
            ))}
          {installations?.length === 0 && (
            <Text style={{ whiteSpace: "nowrap" }}>
              -
              <br />
            </Text>
          )}
        </>
      )
    },
    {
      title: "Action",
      key: "action",
      render: (_, survey: any) => (
        <Space size="middle">
          <>
            <Link to={`${survey?.id}`}>
              <Button icon={<EyeOutlined />} title="View"></Button>
            </Link>
            {survey?.status === "completed" && (
              <Link to={`/${survey?.id}/share-survey`} target="_blank">
                <Button icon={<ShareAltOutlined />} title="Share"></Button>
              </Link>
            )}
          </>
        </Space>
      )
    }
  ];

  const pagination = {
    pageSize: parseInt(querySearchParams.page_size) || 20,
    current: parseInt(querySearchParams.page) || 1,
    total: data?.meta?.total_count,
    showSizeChanger: true,
    pageSizeOptions: ["20", "50", "100", "200"],
    onChange: (page: number, pageSize: number) => {
      const values = {
        page,
        page_size: pageSize
      };

      setQueryParams((old: any) => ({ ...old, ...values }));

      applyPaginationToQueryParams({
        ...queryParams,
        ...values,
        navigate
      });
    }
  };

  const onChange = (key: string) => {
    setTabKey(key);
    if (key === "1") {
      const params: any = queryParams;
      delete params.payment_received;
      setQueryParams(params);
    }
    if (key === "2") {
      const values: any = { payment_received: false };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
    if (key === "3") {
      const values: any = { payment_received: true };
      setQueryParams((oldValues: any) => ({ ...oldValues, ...values }));
    }
  };

  const visibleReportIds = surveys
    .filter((survey: any) => survey?.status === "completed")
    .map((survey: any) => survey?.id);

  const handleBulkReportDownload = async () => {
    try {
      setBulkReportLoading(true);
      const response: any = await bulkCustomerReports({
        resource: "surveys",
        ids: visibleReportIds
      }).unwrap();
      api.success({
        message:
          response?.message ||
          "Request submitted. A download link will be emailed to you shortly.",
        placement: "top",
        duration: 8
      });
    } catch (error: any) {
      api.error({
        message: error?.data?.error || "Failed to prepare download. Please try again.",
        placement: "top"
      });
    } finally {
      setBulkReportLoading(false);
    }
  };

  const handleDownload = async (id: number, filename: string) => {
    try {
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: true
      }));
      const response = await fetch(`${BASE_URL}assignments/${id}/download_zip`, {
        method: "GET",
        headers: {
          "access-token": auth?.accessToken,
          "token-type": "Bearer",
          client: auth.client,
          uid: auth.uid
        }
      });

      if (!response.ok) {
        setDownloadInProgress((prev: any) => ({
          ...prev,
          [id]: false
        }));
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const blob = await response.blob();

      // Create a blob URL for the downloaded data
      const downloadUrl = URL.createObjectURL(blob);

      // Create a link and click it to initiate download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${filename}.zip`;
      link.click();
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: false
      }));
    } catch (error) {
      console.error("Error downloading ZIP:", error);
      setDownloadInProgress((prev: any) => ({
        ...prev,
        [id]: false
      }));
    }
  };

  const uploadOptions = [
    {
      key: "1",
      label: <Link to="upload-invoices">Upload Invoices</Link>
    },
    {
      key: "2",
      label: <Link to="import-payment-details">Upload Payment Details</Link>
    },
    {
      key: "3",
      label: <Link to="upload-survey-invoice-claim">Upload Survey Invoice Claimed Date</Link>
    },
    {
      key: "4",
      label: <Link to="upload-resurvey-invoice-claim">Upload Re-Survey Invoice Claimed Date</Link>
    }
  ];

  if (isUserHasRoleTypes(user, ["super_user", "finance_controller"])) {
    uploadOptions.push({
      key: "5",
      label: <Link to="upload-refund-details">Upload Refund Details</Link>
    });
  }

  return (
    <>
      {contextHolder}
      <Row style={{ marginBottom: 20 }}>
        <Col style={{ display: "flex", justifyContent: "space-between" }} sm={24}>
          <div>
            <h2>Surveys</h2>
            <Text>
              Total Surveys:
              <span style={{ paddingLeft: "4px" }}>{data?.meta?.total_count || 0}</span>
            </Text>
          </div>
          <div style={{ float: "right" }}>
            <Space size="middle" wrap>
              {(isEditorUser || isAdminUser) && (
                <Tooltip
                  title={
                    surveys.length > 50
                      ? "Not available for more than 50 records on the page"
                      : visibleReportIds.length === 0
                        ? "No completed surveys on this page"
                        : ""
                  }
                >
                  <span>
                    <Button
                      size="large"
                      type="primary"
                      icon={<CloudDownloadOutlined />}
                      loading={bulkReportLoading}
                      disabled={surveys.length > 50 || visibleReportIds.length === 0}
                      onClick={handleBulkReportDownload}
                    >
                      Download {visibleReportIds.length} SR Report(s)
                    </Button>
                  </span>
                </Tooltip>
              )}
              {isInvoiceUploadingUser && (
                <Dropdown menu={{ items: uploadOptions }}>
                  <Button size="large" type="primary" icon={<CarOutlined />}>
                    Upload Options
                  </Button>
                </Dropdown>
              )}
            </Space>
          </div>
        </Col>
      </Row>
      {isSuperUser(user) && <Tabs defaultActiveKey={tabKey} items={items} onChange={onChange} />}
      <Filters
        applyFilters={(values: any) =>
          setQueryParams((oldValues: any) => ({ ...oldValues, ...values }))
        }
        surveys
      />
      <TopScrollTable
        rowKey="id"
        columns={isClient ? clientColumns : columns}
        dataSource={surveys}
        bordered
        loading={isLoading || isFetching}
        scroll={{ x: 400 }}
        pagination={pagination}
      />

      <AddPaymentModal
        isModalOpen={showModal}
        headerText={`Survey: ${selectedSurvey?.ecb_survey_code}`}
        assignment={selectedSurvey}
        isRefund={paymentType === "refund"}
        onCancel={onCancel}
        onAddPayment={onAddPayment}
        title="Add"
      />

      {showEmailPreviewModal && (
        <PreviewAndSendEmailModal
          isModalOpen={showEmailPreviewModal}
          assignment={selectedSurvey}
          user={auth?.user}
          title={EMAIL_TYPES[emailType]}
          emailType={emailType}
          onCancel={onCancel}
          onSendEmail={onSendEmail}
        />
      )}

      {showInvoiceUploadModal && (
        <UploadInvoicePdfModal
          isModalOpen={showInvoiceUploadModal}
          assignment={selectedSurvey}
          onCancel={onCancel}
          onUploadInvoicePdf={onUploadInvoicePdf}
        />
      )}
    </>
  );
};

export default Survey;
